/**
 * Strategy AI Service
 * AI-powered strategy recommendations, pillar suggestions, and gap analysis
 */

import { GoogleGenerativeAI } from '@google/generative-ai';
import { PILLAR_PRESETS, AWARENESS_STAGES } from './pillarService';
import { COPY_STYLE_GUIDE } from './copyStyleGuide';

// Initialize Gemini
const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY);
const GEMINI_MODEL = import.meta.env.VITE_GEMINI_MODEL || 'gemini-2.5-flash';

/**
 * Suggest pillars based on brand information
 * @param {Object} brand - Brand data (name, description, products, audience)
 * @param {Object} businessGoals - Business goals (challenge type, customRequest, existingPillars, etc.)
 * @returns {Promise<Array>} Suggested pillars with configurations
 */
export const suggestPillars = async (brand, businessGoals = {}) => {
    try {
        const model = genAI.getGenerativeModel({ model: GEMINI_MODEL });

        // Determine if this is a request for additional pillars
        const isAdditionalRequest = businessGoals.customRequest || businessGoals.existingPillars?.length > 0;
        const pillarCount = isAdditionalRequest ? '2-4' : '4-6';

        // Build context for existing pillars
        const existingPillarsContext = businessGoals.existingPillars?.length > 0
            ? `\nEXISTING PILLARS (do NOT suggest these again, create DIFFERENT ones):\n${businessGoals.existingPillars.map(name => `- ${name}`).join('\n')}`
            : '';

        // Build custom request context
        const customRequestContext = businessGoals.customRequest
            ? `\nSPECIFIC USER REQUEST: The user is specifically looking for pillars related to: "${businessGoals.customRequest}"\nFocus your suggestions on this request while keeping them relevant to the brand.`
            : '';

        const prompt = `You are a marketing strategy expert. Based on the following brand information, suggest ${pillarCount} marketing content pillars (recurring content themes) that would be most effective for their marketing strategy.

Brand Information:
- Name: ${brand.name || 'Unknown'}
- Description: ${brand.description || 'Not provided'}
- Industry: ${brand.industry || 'Not specified'}
- Products/Services: ${brand.products || 'Not specified'}
- Target Audience: ${brand.targetAudience || 'Not specified'}
- Current Challenge: ${businessGoals.currentChallenge || 'new_customers'}
${existingPillarsContext}
${customRequestContext}

Available Pillar Types (use these as inspiration, but customize for the brand):
${PILLAR_PRESETS.map(p => `- ${p.name}: ${p.description}`).join('\n')}

Customer Awareness Stages to consider:
${Object.entries(AWARENESS_STAGES).map(([key, value]) => `- ${value.label}: ${value.description}`).join('\n')}

For each suggested pillar, provide:
1. name: A clear, memorable name (MUST be unique and different from existing pillars)
2. description: 1-2 sentences explaining what content fits this pillar
3. color: A hex color code (choose from: #10B981, #F59E0B, #8B5CF6, #3B82F6, #EC4899, #EF4444, #6366F1, #14B8A6)
4. icon: One of (GraduationCap, Sparkles, Camera, Package, Tag, Lightbulb, Target, MessageSquare, Heart, Users, Zap, TrendingUp)
5. awarenessStages: Which awareness stages this pillar serves best (unaware, problemAware, solutionAware, productAware, mostAware)
6. exampleTopics: 3-4 specific content ideas for this brand

Return your response as a JSON array only, no additional text:
[
  {
    "name": "Pillar Name",
    "description": "Description",
    "color": "#hexcode",
    "icon": "IconName",
    "awarenessStages": ["stage1", "stage2"],
    "exampleTopics": ["topic1", "topic2", "topic3"]
  }
]

${COPY_STYLE_GUIDE}`;

        const result = await model.generateContent(prompt);
        const response = await result.response;
        const text = response.text();

        // Parse JSON from response
        const jsonMatch = text.match(/\[[\s\S]*\]/);
        if (!jsonMatch) {
            console.error('Failed to parse pillar suggestions:', text);
            return getDefaultPillarSuggestions(businessGoals);
        }

        const suggestions = JSON.parse(jsonMatch[0]);

        // Transform to full pillar format with awareness mappings
        return suggestions.map(suggestion => ({
            name: suggestion.name,
            description: suggestion.description,
            color: suggestion.color || '#6366F1',
            icon: suggestion.icon || 'Lightbulb',
            contentGuidelines: {
                toneOfVoice: [],
                exampleTopics: suggestion.exampleTopics || [],
                keyMessages: []
            },
            awarenessMapping: buildAwarenessMapping(suggestion.awarenessStages || []),
            frequency: {
                currentWeight: Math.round(100 / suggestions.length),
                recommendedWeight: Math.round(100 / suggestions.length),
                postsPerWeek: 2
            }
        }));
    } catch (error) {
        console.error('Error suggesting pillars:', error);
        return getDefaultPillarSuggestions(businessGoals);
    }
};

/**
 * Build awareness mapping from stage names
 */
const buildAwarenessMapping = (stages) => {
    const mapping = {
        unaware: { recommended: false, weight: 0 },
        problemAware: { recommended: false, weight: 0 },
        solutionAware: { recommended: false, weight: 0 },
        productAware: { recommended: false, weight: 0 },
        mostAware: { recommended: false, weight: 0 }
    };

    stages.forEach(stage => {
        if (mapping[stage]) {
            mapping[stage].recommended = true;
            mapping[stage].weight = Math.round(100 / stages.length);
        }
    });

    return mapping;
};

/**
 * Get default pillar suggestions based on business challenge
 */
const getDefaultPillarSuggestions = (businessGoals) => {
    const challenge = businessGoals?.currentChallenge || 'new_customers';

    const challengePillars = {
        new_customers: ['Education', 'Entertainment', 'Behind the Scenes', 'Social Proof'],
        repeat_purchases: ['Social Proof', 'Product Features', 'Offers & Promotions', 'Education'],
        brand_building: ['Entertainment', 'Behind the Scenes', 'Education', 'Social Proof'],
        market_expansion: ['Education', 'Social Proof', 'Product Features', 'Behind the Scenes']
    };

    const pillarNames = challengePillars[challenge] || challengePillars.new_customers;

    return pillarNames.map(name => {
        const preset = PILLAR_PRESETS.find(p => p.name === name);
        if (preset) {
            return {
                ...preset,
                frequency: {
                    currentWeight: Math.round(100 / pillarNames.length),
                    recommendedWeight: Math.round(100 / pillarNames.length),
                    postsPerWeek: 2
                }
            };
        }
        return null;
    }).filter(Boolean);
};

/**
 * Analyze content gaps based on scheduled posts and pillars
 * @param {Array} scheduledPosts - Posts from last 30 days
 * @param {Array} pillars - User's pillars
 * @param {Object} strategy - Brand strategy with goals
 * @returns {Promise<Array>} Gap analysis with recommendations
 */
export const analyzeContentGaps = async (scheduledPosts, pillars, strategy) => {
    const gaps = [];

    if (!pillars || pillars.length === 0) {
        gaps.push({
            type: 'no_pillars',
            severity: 'critical',
            message: 'No marketing pillars defined',
            suggestion: 'Define at least 3 content pillars to guide your strategy',
            action: 'Add Pillars',
            actionType: 'create_pillar'
        });
        return gaps;
    }

    // Calculate pillar usage from posts
    const pillarUsage = {};
    pillars.forEach(p => {
        pillarUsage[p.id] = {
            pillar: p,
            count: 0,
            lastUsed: null
        };
    });

    scheduledPosts.forEach(post => {
        if (post.pillarId && pillarUsage[post.pillarId]) {
            pillarUsage[post.pillarId].count++;
            const postDate = post.scheduledDate?.toDate?.() || new Date(post.scheduledDate);
            if (!pillarUsage[post.pillarId].lastUsed || postDate > pillarUsage[post.pillarId].lastUsed) {
                pillarUsage[post.pillarId].lastUsed = postDate;
            }
        }
    });

    const totalPosts = scheduledPosts.length;

    // Check for underused pillars
    Object.values(pillarUsage).forEach(({ pillar, count, lastUsed }) => {
        const expectedPercentage = pillar.frequency?.currentWeight || (100 / pillars.length);
        const actualPercentage = totalPosts > 0 ? (count / totalPosts) * 100 : 0;

        // Pillar is significantly underused (less than half expected)
        if (actualPercentage < expectedPercentage / 2) {
            gaps.push({
                type: 'underused_pillar',
                severity: actualPercentage === 0 ? 'high' : 'medium',
                pillarId: pillar.id,
                pillarName: pillar.name,
                pillarColor: pillar.color,
                message: `"${pillar.name}" content is underrepresented (${Math.round(actualPercentage)}% vs ${Math.round(expectedPercentage)}% target)`,
                suggestion: `Create more "${pillar.name}" content to maintain balance`,
                action: `Create ${pillar.name}`,
                actionType: 'create_content',
                data: { pillarId: pillar.id }
            });
        }

        // Pillar hasn't been used recently (more than 7 days)
        const daysSinceLastUse = lastUsed
            ? Math.floor((Date.now() - lastUsed.getTime()) / (1000 * 60 * 60 * 24))
            : 999;

        if (daysSinceLastUse > 7 && count > 0) {
            gaps.push({
                type: 'stale_pillar',
                severity: 'low',
                pillarId: pillar.id,
                pillarName: pillar.name,
                pillarColor: pillar.color,
                message: `No "${pillar.name}" content scheduled in the last ${daysSinceLastUse} days`,
                suggestion: `Schedule fresh "${pillar.name}" content`,
                action: `Schedule ${pillar.name}`,
                actionType: 'schedule_content',
                data: { pillarId: pillar.id }
            });
        }
    });

    // Check funnel stage coverage
    const funnelCoverage = { TOFU: 0, MOFU: 0, BOFU: 0 };
    pillars.forEach(pillar => {
        if (pillar.funnelMapping?.TOFU?.recommended) funnelCoverage.TOFU++;
        if (pillar.funnelMapping?.MOFU?.recommended) funnelCoverage.MOFU++;
        if (pillar.funnelMapping?.BOFU?.recommended) funnelCoverage.BOFU++;
    });

    if (funnelCoverage.TOFU === 0) {
        gaps.push({
            type: 'funnel_gap',
            severity: 'high',
            stage: 'TOFU',
            message: 'No pillars targeting Top of Funnel (Awareness)',
            suggestion: 'Add pillars that target unaware or problem-aware audiences',
            action: 'Fix TOFU',
            actionType: 'edit_pillars'
        });
    }

    if (funnelCoverage.BOFU === 0) {
        gaps.push({
            type: 'funnel_gap',
            severity: 'high',
            stage: 'BOFU',
            message: 'No pillars targeting Bottom of Funnel (Conversion)',
            suggestion: 'Add pillars with offers, testimonials, or urgency content',
            action: 'Fix BOFU',
            actionType: 'edit_pillars'
        });
    }

    // Check if revenue goals require more BOFU content
    if (strategy?.businessGoals?.monthlyRevenueTarget > 0) {
        const bofuPillars = pillars.filter(p => p.funnelMapping?.BOFU?.recommended);
        const bofuWeight = bofuPillars.reduce((sum, p) => sum + (p.frequency?.currentWeight || 0), 0);

        if (bofuWeight < 20) {
            gaps.push({
                type: 'revenue_alignment',
                severity: 'medium',
                message: 'Revenue-driving content (BOFU) is underweighted',
                suggestion: 'Increase weight of conversion-focused pillars to hit revenue targets',
                action: 'Increase BOFU',
                actionType: 'adjust_weights'
            });
        }
    }

    // Sort by severity
    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    gaps.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

    return gaps;
};

/**
 * Calculate optimal pillar frequency based on performance
 * @param {Array} pillars - User's pillars with performance data
 * @param {Object} strategy - Brand strategy with goals
 * @returns {Promise<Object>} Recommended frequencies per pillar
 */
export const calculateOptimalFrequency = async (pillars, strategy) => {
    if (!pillars || pillars.length === 0) {
        return { recommendations: [], reasoning: 'No pillars defined' };
    }

    const recommendations = [];
    const challenge = strategy?.businessGoals?.currentChallenge || 'new_customers';

    // Calculate performance scores
    const pillarScores = pillars.map(pillar => {
        let score = 50; // Base score

        // Boost based on engagement (pipeline metrics)
        const engagement = pillar.performance?.pipelineMetrics?.avgEngagementRate || 0;
        if (engagement > 5) score += 20;
        else if (engagement > 2) score += 10;

        // Boost based on ROAS (revenue metrics)
        const roas = pillar.performance?.revenueMetrics?.avgROAS || 0;
        if (roas > 3) score += 30;
        else if (roas > 1.5) score += 15;

        // Adjust based on challenge alignment
        const funnelFocus = {
            new_customers: 'TOFU',
            repeat_purchases: 'BOFU',
            brand_building: 'TOFU',
            market_expansion: 'TOFU'
        }[challenge];

        if (pillar.funnelMapping?.[funnelFocus]?.recommended) {
            score += 10;
        }

        return { pillar, score };
    });

    // Normalize scores to 100%
    const totalScore = pillarScores.reduce((sum, ps) => sum + ps.score, 0);

    pillarScores.forEach(({ pillar, score }) => {
        const recommendedWeight = Math.round((score / totalScore) * 100);
        const currentWeight = pillar.frequency?.currentWeight || 0;
        const change = recommendedWeight - currentWeight;

        recommendations.push({
            pillarId: pillar.id,
            pillarName: pillar.name,
            pillarColor: pillar.color,
            currentWeight,
            recommendedWeight,
            change,
            changeDirection: change > 5 ? 'increase' : change < -5 ? 'decrease' : 'maintain',
            reasoning: getFrequencyReasoning(pillar, score, challenge)
        });
    });

    return {
        recommendations,
        reasoning: `Optimized for ${challenge.replace('_', ' ')} based on pillar performance`
    };
};

/**
 * Get reasoning for frequency recommendation
 */
const getFrequencyReasoning = (pillar, score, challenge) => {
    const reasons = [];

    const engagement = pillar.performance?.pipelineMetrics?.avgEngagementRate || 0;
    const roas = pillar.performance?.revenueMetrics?.avgROAS || 0;

    if (engagement > 5) {
        reasons.push('High engagement rate');
    } else if (engagement < 1) {
        reasons.push('Low engagement - consider refreshing content approach');
    }

    if (roas > 3) {
        reasons.push('Strong ROAS performance');
    } else if (roas > 0 && roas < 1) {
        reasons.push('Below-target ROAS');
    }

    const funnelFocus = {
        new_customers: 'TOFU',
        brand_building: 'TOFU',
        repeat_purchases: 'BOFU',
        market_expansion: 'TOFU'
    }[challenge];

    if (pillar.funnelMapping?.[funnelFocus]?.recommended) {
        reasons.push(`Aligned with ${challenge.replace('_', ' ')} focus`);
    }

    return reasons.length > 0 ? reasons.join('. ') : 'Baseline recommendation';
};

/**
 * Suggest pillar for a creative based on its brief
 * @param {Object} creativeBrief - The creative brief
 * @param {Array} pillars - Available pillars
 * @param {string} funnelStage - Optional funnel stage context
 * @returns {Promise<Object>} Suggested pillar with confidence
 */
export const suggestPillarForCreative = async (creativeBrief, pillars, funnelStage = null) => {
    if (!pillars || pillars.length === 0) {
        return { suggestedPillar: null, confidence: 0, reasoning: 'No pillars available' };
    }

    try {
        const model = genAI.getGenerativeModel({ model: GEMINI_MODEL });

        const prompt = `You are a marketing expert. Based on the creative brief below, suggest which marketing pillar would be the best fit.

Creative Brief:
- Product: ${creativeBrief.product || 'Not specified'}
- Target Audience: ${creativeBrief.targetAudience || 'Not specified'}
- Objective: ${creativeBrief.objective || 'Not specified'}
- Marketing Angle: ${creativeBrief.marketingAngle || 'Not specified'}
- Tone: ${creativeBrief.tone || 'Not specified'}
${funnelStage ? `- Funnel Stage: ${funnelStage}` : ''}

Available Pillars:
${pillars.map((p, i) => `${i + 1}. ${p.name}: ${p.description}`).join('\n')}

Return ONLY a JSON object with:
{
  "pillarIndex": <number 0-${pillars.length - 1}>,
  "confidence": <number 0-100>,
  "reasoning": "<brief explanation>"
}`;

        const result = await model.generateContent(prompt);
        const response = await result.response;
        const text = response.text();

        const jsonMatch = text.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
            return fallbackPillarSuggestion(creativeBrief, pillars, funnelStage);
        }

        const suggestion = JSON.parse(jsonMatch[0]);
        const suggestedPillar = pillars[suggestion.pillarIndex];

        return {
            suggestedPillar,
            confidence: suggestion.confidence,
            reasoning: suggestion.reasoning
        };
    } catch (error) {
        console.error('Error suggesting pillar for creative:', error);
        return fallbackPillarSuggestion(creativeBrief, pillars, funnelStage);
    }
};

/**
 * Fallback pillar suggestion based on keywords
 */
const fallbackPillarSuggestion = (creativeBrief, pillars, funnelStage) => {
    const text = `${creativeBrief.product || ''} ${creativeBrief.objective || ''} ${creativeBrief.marketingAngle || ''}`.toLowerCase();

    // Simple keyword matching
    const keywords = {
        education: ['learn', 'how to', 'guide', 'tips', 'tutorial', 'teach'],
        entertainment: ['fun', 'trend', 'viral', 'meme', 'challenge', 'humor'],
        social_proof: ['testimonial', 'review', 'case study', 'customer', 'success', 'result'],
        product: ['feature', 'benefit', 'new', 'launch', 'product', 'service'],
        offers: ['sale', 'discount', 'offer', 'limited', 'deal', 'promo']
    };

    let bestMatch = pillars[0];
    let bestScore = 0;

    pillars.forEach(pillar => {
        let score = 0;
        const pillarType = pillar.name.toLowerCase().replace(/\s+/g, '_');

        // Check keyword matches
        Object.entries(keywords).forEach(([type, words]) => {
            if (pillarType.includes(type.split('_')[0])) {
                words.forEach(word => {
                    if (text.includes(word)) score += 10;
                });
            }
        });

        // Bonus for funnel stage match
        if (funnelStage && pillar.funnelMapping?.[funnelStage]?.recommended) {
            score += 20;
        }

        if (score > bestScore) {
            bestScore = score;
            bestMatch = pillar;
        }
    });

    return {
        suggestedPillar: bestMatch,
        confidence: Math.min(bestScore * 2, 80),
        reasoning: 'Based on content keywords and funnel stage alignment'
    };
};

/**
 * Generate content ideas for a pillar
 * @param {Object} pillar - The pillar
 * @param {Object} brand - Brand information
 * @param {number} count - Number of ideas to generate
 * @returns {Promise<Array>} Content ideas
 */
export const generateContentIdeas = async (pillar, brand, count = 5) => {
    try {
        const model = genAI.getGenerativeModel({ model: GEMINI_MODEL });

        const prompt = `Generate ${count} specific content ideas for a marketing pillar.

Brand: ${brand.name || 'Brand'}
Industry: ${brand.industry || 'Not specified'}
Products: ${brand.products || 'Not specified'}
Target Audience: ${brand.targetAudience || 'General audience'}

Pillar: ${pillar.name}
Description: ${pillar.description || 'General content theme'}
Tone: ${pillar.contentGuidelines?.toneOfVoice?.join(', ') || 'Professional'}

For each idea, provide:
1. title: A catchy headline/title
2. format: The content format (video, carousel, image, story, reel)
3. hook: The opening hook or first line
4. callToAction: What action viewers should take

Return as JSON array only:
[
  {
    "title": "...",
    "format": "...",
    "hook": "...",
    "callToAction": "..."
  }
]

${COPY_STYLE_GUIDE}`;

        const result = await model.generateContent(prompt);
        const response = await result.response;
        const text = response.text();

        const jsonMatch = text.match(/\[[\s\S]*\]/);
        if (!jsonMatch) {
            return getDefaultContentIdeas(pillar);
        }

        return JSON.parse(jsonMatch[0]);
    } catch (error) {
        console.error('Error generating content ideas:', error);
        return getDefaultContentIdeas(pillar);
    }
};

/**
 * Get default content ideas
 */
const getDefaultContentIdeas = (pillar) => {
    const ideas = {
        Education: [
            { title: '5 Tips You Need to Know', format: 'carousel', hook: 'Stop making these mistakes...', callToAction: 'Save for later' },
            { title: 'How It Actually Works', format: 'video', hook: 'Let me break this down for you...', callToAction: 'Follow for more tips' }
        ],
        Entertainment: [
            { title: 'When You Finally Get It', format: 'reel', hook: 'POV: The moment it clicks', callToAction: 'Tag someone who needs this' },
            { title: 'Expectation vs Reality', format: 'carousel', hook: 'What I thought vs what happened...', callToAction: 'Drop a comment' }
        ],
        'Social Proof': [
            { title: 'Customer Transformation', format: 'video', hook: 'See what happened after 30 days...', callToAction: 'Start your journey' },
            { title: 'Real Results, Real People', format: 'carousel', hook: 'Their words, not ours...', callToAction: 'Join them today' }
        ]
    };

    return ideas[pillar.name] || [
        { title: 'Engaging Content Idea', format: 'image', hook: 'Something your audience needs to see...', callToAction: 'Learn more' }
    ];
};

/**
 * Generate a complete pillar from a user's description/idea
 * @param {Object} brand - Brand data
 * @param {string} userDescription - User's description of the pillar they want
 * @returns {Promise<Object>} Complete pillar data
 */
export const generatePillarFromDescription = async (brand, userDescription) => {
    try {
        const model = genAI.getGenerativeModel({ model: GEMINI_MODEL });

        const prompt = `You are a marketing strategy expert. Based on the brand information and the user's pillar idea, generate a complete marketing content pillar configuration.

Brand Information:
- Name: ${brand.name || 'Unknown Brand'}
- Description: ${brand.description || 'Not provided'}
- Industry: ${brand.industry || 'Not specified'}
- Products/Services: ${brand.products || 'Not specified'}
- Target Audience: ${brand.targetAudience || 'Not specified'}

User's Pillar Idea/Description:
"${userDescription}"

Generate a complete pillar with:
1. name: A clear, memorable pillar name (2-4 words)
2. description: A 1-2 sentence description of what content fits this pillar
3. color: Pick the most fitting hex color from: #10B981 (green/growth), #F59E0B (amber/energy), #8B5CF6 (purple/creative), #3B82F6 (blue/trust), #EC4899 (pink/emotion), #EF4444 (red/urgency), #6366F1 (indigo/premium), #14B8A6 (teal/fresh)
4. icon: Best matching icon from: GraduationCap (education), Sparkles (entertainment), Camera (behind scenes), Package (product), Tag (offers), Lightbulb (ideas), Target (goals), MessageSquare (social), Heart (lifestyle), Users (community), Zap (action), TrendingUp (growth)
5. awarenessStages: Array of which stages this pillar best serves. Options: unaware, problemAware, solutionAware, productAware, mostAware
6. awarenessWeights: Object mapping each selected stage to a weight percentage (should sum to ~100 for selected stages)
7. toneOfVoice: Array of 3-5 tone descriptors for this pillar (e.g., "informative", "playful", "inspiring")
8. exampleTopics: Array of 5-7 specific content ideas tailored to this brand
9. keyMessages: Array of 3-4 core messages this pillar should reinforce

Return ONLY a valid JSON object:
{
  "name": "Pillar Name",
  "description": "Description of the pillar",
  "color": "#hexcode",
  "icon": "IconName",
  "awarenessStages": ["stage1", "stage2"],
  "awarenessWeights": {"stage1": 60, "stage2": 40},
  "toneOfVoice": ["tone1", "tone2", "tone3"],
  "exampleTopics": ["topic1", "topic2", "topic3", "topic4", "topic5"],
  "keyMessages": ["message1", "message2", "message3"]
}

${COPY_STYLE_GUIDE}`;

        const result = await model.generateContent(prompt);
        const response = await result.response;
        const text = response.text();

        // Parse JSON from response
        const jsonMatch = text.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
            console.error('Failed to parse AI pillar response:', text);
            throw new Error('Failed to generate pillar configuration');
        }

        const aiResult = JSON.parse(jsonMatch[0]);

        // Transform to full pillar format
        const awarenessMapping = {
            unaware: { recommended: false, weight: 0 },
            problemAware: { recommended: false, weight: 0 },
            solutionAware: { recommended: false, weight: 0 },
            productAware: { recommended: false, weight: 0 },
            mostAware: { recommended: false, weight: 0 }
        };

        // Apply AI-suggested awareness stages and weights
        (aiResult.awarenessStages || []).forEach(stage => {
            if (awarenessMapping[stage] !== undefined) {
                awarenessMapping[stage].recommended = true;
                awarenessMapping[stage].weight = aiResult.awarenessWeights?.[stage] || Math.round(100 / aiResult.awarenessStages.length);
            }
        });

        return {
            name: aiResult.name || 'New Pillar',
            description: aiResult.description || '',
            color: aiResult.color || '#6366F1',
            icon: aiResult.icon || 'Lightbulb',
            awarenessMapping,
            contentGuidelines: {
                toneOfVoice: aiResult.toneOfVoice || [],
                exampleTopics: aiResult.exampleTopics || [],
                keyMessages: aiResult.keyMessages || []
            },
            frequency: {
                currentWeight: 25,
                recommendedWeight: 25,
                postsPerWeek: 2
            }
        };
    } catch (error) {
        console.error('Error generating pillar from description:', error);
        throw error;
    }
};

export default {
    suggestPillars,
    analyzeContentGaps,
    calculateOptimalFrequency,
    suggestPillarForCreative,
    generateContentIdeas,
    generatePillarFromDescription
};
